import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Settings, Building2, Upload, Save, 
  Palette, FileText, Phone, MapPin,
  Cloud, CloudOff, Download, RefreshCw, Check,
  FileUp, Loader2, Eye, X
} from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  
  // Template extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showExtractedPreview, setShowExtractedPreview] = useState(false);
  
  const [config, setConfig] = useState({
    logo_url: '',
    nome_clinica: '',
    cnes: '',
    endereco: '',
    cidade: '',
    telefone: '',
    rodape_personalizado: '',
    assinatura_digital: '',
    tema: 'light',
    cor_primaria: '#0F766E',
    cor_secundaria: '#5FB3A2'
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'administrador' && !parsedUser.is_tenant_admin) {
        toast.error('Apenas administradores podem acessar configurações');
        navigate('/');
        return;
      }
      setUser(parsedUser);
      loadTenantData();
      checkDriveStatus();
      loadBackups();
      
      // Check for OAuth callback
      if (searchParams.get('drive_connected') === 'true') {
        toast.success('Google Drive conectado com sucesso!');
        setDriveConnected(true);
      }
      if (searchParams.get('drive_error')) {
        toast.error(`Erro ao conectar: ${searchParams.get('drive_error')}`);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const loadTenantData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenant/current`, getAuthHeaders());
      setTenant(response.data);
      if (response.data.configuracoes) {
        setConfig(prev => ({ ...prev, ...response.data.configuracoes }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações da clínica');
    } finally {
      setLoading(false);
    }
  };

  const checkDriveStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/drive/status?authorization=Bearer ${token}`);
      setDriveConnected(response.data.connected);
    } catch (error) {
      console.error('Erro ao verificar Drive:', error);
    }
  };

  const loadBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/drive/backups?authorization=Bearer ${token}`);
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Erro ao carregar backups:', error);
    }
  };

  const handleConnectDrive = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/drive/connect?authorization=Bearer ${token}`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Erro ao conectar Drive:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar Google Drive');
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o Google Drive?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/drive/disconnect?authorization=Bearer ${token}`);
      setDriveConnected(false);
      toast.success('Google Drive desconectado');
    } catch (error) {
      toast.error('Erro ao desconectar');
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/drive/backup?authorization=Bearer ${token}`);
      toast.success('Backup realizado com sucesso!');
      loadBackups();
    } catch (error) {
      console.error('Erro ao criar backup:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/tenant/config`, config, getAuthHeaders());
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // For now, use a data URL (in production, you'd upload to cloud storage)
    const reader = new FileReader();
    reader.onload = () => {
      setConfig(prev => ({ ...prev, logo_url: reader.result }));
      toast.success('Logo carregada!');
    };
    reader.readAsDataURL(file);
  };

  // Template extraction from PDF/Image
  const handleTemplateExtraction = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !['pdf', 'png', 'jpg', 'jpeg'].includes(ext || '')) {
      toast.error('Tipo de arquivo não suportado. Use PDF, PNG ou JPEG.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }

    setExtracting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (tenant?.id) {
        formData.append('tenant_id', tenant.id);
      }

      const response = await axios.post(`${API}/template-extract/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setExtractedData(response.data);
        setShowExtractedPreview(true);
        toast.success('Arquivo processado! Revise os dados extraídos.');
      }
    } catch (error) {
      console.error('Erro na extração:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo');
    } finally {
      setExtracting(false);
      // Reset input
      e.target.value = '';
    }
  };

  const applyExtractedData = () => {
    if (!extractedData) return;

    const data = extractedData.extracted_data || {};
    
    setConfig(prev => ({
      ...prev,
      nome_clinica: data.nome_clinica || prev.nome_clinica,
      cnes: data.cnes || prev.cnes,
      telefone: data.telefone || prev.telefone,
      endereco: data.endereco || prev.endereco,
      cidade: data.cidade || prev.cidade,
      rodape_personalizado: data.rodape || prev.rodape_personalizado,
      // Use header image as logo if available
      logo_url: extractedData.header_image 
        ? `data:image/png;base64,${extractedData.header_image}` 
        : prev.logo_url
    }));

    setShowExtractedPreview(false);
    toast.success('Dados aplicados! Clique em Salvar para confirmar.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7F8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F766E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F8]">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-[#0F766E]" />
            <h1 className="text-2xl font-bold text-[#1F2933]">Configurações da Clínica</h1>
          </div>
          <p className="text-[#64748B]">
            Personalize o template de documentos e receitas
          </p>
        </div>

        {/* Tenant Info Card */}
        {tenant && (
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-[#0F766E] p-3 rounded-xl">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#1F2933]">{tenant.nome}</h2>
                <p className="text-sm text-[#64748B]">
                  Plano: <span className="capitalize font-medium">{tenant.plano}</span>
                </p>
                <p className="text-xs text-[#64748B]">
                  URL: <code className="bg-[#F4F7F8] px-2 py-0.5 rounded">/{tenant.slug}</code>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* IMPORT FROM FILE - New Section */}
        <div className="bg-gradient-to-r from-[#0F766E] to-[#0D6660] rounded-xl shadow-sm p-6 mb-6 text-white">
          <div className="flex items-start gap-4">
            <div className="bg-white/20 p-3 rounded-xl">
              <FileUp className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Importar Template de Arquivo</h3>
              <p className="text-white/80 text-sm mb-4">
                Carregue um PDF ou imagem (PNG/JPEG) de um documento existente da clínica. 
                O sistema irá extrair automaticamente o cabeçalho, rodapé, CNES, telefone e endereço.
              </p>
              
              <div className="flex items-center gap-4">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    onChange={handleTemplateExtraction}
                    className="hidden"
                    disabled={extracting}
                    data-testid="template-file-input"
                  />
                  <div className="bg-white text-[#0F766E] px-4 py-2 rounded-lg font-medium cursor-pointer hover:bg-white/90 transition-colors flex items-center justify-center gap-2">
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Carregar PDF ou Imagem
                      </>
                    )}
                  </div>
                </label>
              </div>
              
              <p className="text-white/60 text-xs mt-2">
                Formatos aceitos: PDF, PNG, JPEG. Máximo: 10MB
              </p>
            </div>
          </div>
        </div>

        {/* Extracted Data Preview Modal */}
        {showExtractedPreview && extractedData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-[#D1D5DB] flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#1F2933]">
                    Dados Extraídos do Arquivo
                  </h2>
                  <p className="text-sm text-[#64748B]">
                    {extractedData.filename} ({extractedData.file_type?.toUpperCase()})
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowExtractedPreview(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-6 grid md:grid-cols-2 gap-6">
                {/* Header Preview */}
                <div>
                  <h4 className="text-sm font-medium text-[#64748B] mb-2">Cabeçalho Extraído</h4>
                  {extractedData.header_image && (
                    <div className="border border-[#D1D5DB] rounded-lg p-2 bg-[#F4F7F8]">
                      <img 
                        src={`data:image/png;base64,${extractedData.header_image}`} 
                        alt="Header" 
                        className="w-full object-contain"
                      />
                    </div>
                  )}
                  {extractedData.header_text && (
                    <div className="mt-2 p-2 bg-[#F4F7F8] rounded text-xs text-[#64748B] max-h-24 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{extractedData.header_text}</pre>
                    </div>
                  )}
                </div>
                
                {/* Footer Preview */}
                <div>
                  <h4 className="text-sm font-medium text-[#64748B] mb-2">Rodapé Extraído</h4>
                  {extractedData.footer_image && (
                    <div className="border border-[#D1D5DB] rounded-lg p-2 bg-[#F4F7F8]">
                      <img 
                        src={`data:image/png;base64,${extractedData.footer_image}`} 
                        alt="Footer" 
                        className="w-full object-contain"
                      />
                    </div>
                  )}
                  {extractedData.footer_text && (
                    <div className="mt-2 p-2 bg-[#F4F7F8] rounded text-xs text-[#64748B] max-h-24 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{extractedData.footer_text}</pre>
                    </div>
                  )}
                </div>
                
                {/* Extracted Data */}
                <div className="md:col-span-2">
                  <h4 className="text-sm font-medium text-[#64748B] mb-3">Dados Identificados</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 bg-[#F4F7F8] rounded-lg">
                      <p className="text-xs text-[#64748B] mb-1">Nome da Clínica</p>
                      <p className="font-medium text-[#1F2933] text-sm">
                        {extractedData.extracted_data?.nome_clinica || '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#F4F7F8] rounded-lg">
                      <p className="text-xs text-[#64748B] mb-1">CNES</p>
                      <p className="font-medium text-[#1F2933] text-sm">
                        {extractedData.extracted_data?.cnes || '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#F4F7F8] rounded-lg">
                      <p className="text-xs text-[#64748B] mb-1">Telefone</p>
                      <p className="font-medium text-[#1F2933] text-sm">
                        {extractedData.extracted_data?.telefone || '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#F4F7F8] rounded-lg md:col-span-2">
                      <p className="text-xs text-[#64748B] mb-1">Endereço</p>
                      <p className="font-medium text-[#1F2933] text-sm">
                        {extractedData.extracted_data?.endereco || '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#F4F7F8] rounded-lg">
                      <p className="text-xs text-[#64748B] mb-1">Cidade</p>
                      <p className="font-medium text-[#1F2933] text-sm">
                        {extractedData.extracted_data?.cidade || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-[#D1D5DB] flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowExtractedPreview(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={applyExtractedData}
                  className="bg-[#0F766E] hover:bg-[#0D6660]"
                  data-testid="apply-extracted-data-button"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aplicar Dados Extraídos
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Logo Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Logo da Clínica
            </h3>
            
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 bg-[#F4F7F8] rounded-xl flex items-center justify-center border-2 border-dashed border-[#D1D5DB]">
                {config.logo_url ? (
                  <img 
                    src={config.logo_url} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : (
                  <Building2 className="w-12 h-12 text-[#D1D5DB]" />
                )}
              </div>
              
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="mb-2"
                  data-testid="logo-upload-input"
                />
                <p className="text-xs text-[#64748B]">
                  PNG, JPG ou SVG. Máximo 2MB.
                </p>
              </div>
            </div>
          </div>

          {/* Identification Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Identificação
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome_clinica">Nome da Clínica</Label>
                <Input
                  id="nome_clinica"
                  value={config.nome_clinica}
                  onChange={(e) => setConfig(prev => ({ ...prev, nome_clinica: e.target.value }))}
                  placeholder="Nome exibido nos documentos"
                  data-testid="nome-clinica-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cnes">CNES</Label>
                <Input
                  id="cnes"
                  value={config.cnes}
                  onChange={(e) => setConfig(prev => ({ ...prev, cnes: e.target.value }))}
                  placeholder="Código CNES"
                  data-testid="cnes-input"
                />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Endereço
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="endereco">Endereço Completo</Label>
                <Input
                  id="endereco"
                  value={config.endereco}
                  onChange={(e) => setConfig(prev => ({ ...prev, endereco: e.target.value }))}
                  placeholder="Rua, número, bairro..."
                  data-testid="endereco-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={config.cidade}
                  onChange={(e) => setConfig(prev => ({ ...prev, cidade: e.target.value }))}
                  placeholder="Cidade - UF"
                  data-testid="cidade-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={config.telefone}
                  onChange={(e) => setConfig(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="(00) 00000-0000"
                  data-testid="telefone-input"
                />
              </div>
            </div>
          </div>

          {/* Document Template Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Template de Documentos
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="rodape">Rodapé Personalizado</Label>
                <Textarea
                  id="rodape"
                  value={config.rodape_personalizado}
                  onChange={(e) => setConfig(prev => ({ ...prev, rodape_personalizado: e.target.value }))}
                  placeholder="Texto que aparecerá no rodapé de todos os documentos..."
                  rows={3}
                  data-testid="rodape-input"
                />
              </div>
              
              <div>
                <Label htmlFor="assinatura">Assinatura Digital</Label>
                <Textarea
                  id="assinatura"
                  value={config.assinatura_digital}
                  onChange={(e) => setConfig(prev => ({ ...prev, assinatura_digital: e.target.value }))}
                  placeholder="Texto da assinatura digital (ex: Dr. Nome - CRM 00000)"
                  rows={2}
                  data-testid="assinatura-input"
                />
              </div>
            </div>
          </div>

          {/* Theme Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Tema e Cores
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="tema">Tema</Label>
                <select
                  id="tema"
                  value={config.tema}
                  onChange={(e) => setConfig(prev => ({ ...prev, tema: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-[#D1D5DB] bg-white"
                  data-testid="tema-select"
                >
                  <option value="light">Claro</option>
                  <option value="dark">Escuro</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="cor_primaria">Cor Primária</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="cor_primaria"
                    value={config.cor_primaria}
                    onChange={(e) => setConfig(prev => ({ ...prev, cor_primaria: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                    data-testid="cor-primaria-input"
                  />
                  <Input
                    value={config.cor_primaria}
                    onChange={(e) => setConfig(prev => ({ ...prev, cor_primaria: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="cor_secundaria">Cor Secundária</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="cor_secundaria"
                    value={config.cor_secundaria}
                    onChange={(e) => setConfig(prev => ({ ...prev, cor_secundaria: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                    data-testid="cor-secundaria-input"
                  />
                  <Input
                    value={config.cor_secundaria}
                    onChange={(e) => setConfig(prev => ({ ...prev, cor_secundaria: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4">
              Pré-visualização do Cabeçalho
            </h3>
            
            <div className="border border-[#D1D5DB] rounded-lg p-6 bg-white">
              <div className="flex items-center justify-between border-b border-[#D1D5DB] pb-4">
                <div className="flex items-center gap-4">
                  {config.logo_url ? (
                    <img src={config.logo_url} alt="Logo" className="h-16 object-contain" />
                  ) : (
                    <div className="w-16 h-16 bg-[#F4F7F8] rounded-lg flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-[#D1D5DB]" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold" style={{ color: config.cor_primaria }}>
                      {config.nome_clinica || 'Nome da Clínica'}
                    </h4>
                    {config.cnes && (
                      <p className="text-sm text-[#64748B]">CNES: {config.cnes}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="pt-4 text-sm text-[#64748B]">
                {config.endereco && <p>{config.endereco}</p>}
                <p>
                  {config.cidade && config.cidade}
                  {config.telefone && ` - Tel: ${config.telefone}`}
                </p>
              </div>
              
              {config.rodape_personalizado && (
                <div className="mt-4 pt-4 border-t border-[#D1D5DB] text-xs text-[#64748B]">
                  {config.rodape_personalizado}
                </div>
              )}
            </div>
          </div>

          {/* Google Drive Backup Section */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6">
            <h3 className="text-lg font-semibold text-[#1F2933] mb-4 flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Backup Google Drive
            </h3>
            
            <div className="space-y-4">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-4 bg-[#F4F7F8] rounded-lg">
                <div className="flex items-center gap-3">
                  {driveConnected ? (
                    <>
                      <div className="bg-green-100 p-2 rounded-full">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1F2933]">Google Drive Conectado</p>
                        <p className="text-sm text-[#64748B]">Backups automáticos habilitados</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-gray-200 p-2 rounded-full">
                        <CloudOff className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1F2933]">Drive não conectado</p>
                        <p className="text-sm text-[#64748B]">Conecte para habilitar backups</p>
                      </div>
                    </>
                  )}
                </div>
                
                {driveConnected ? (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectDrive}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="disconnect-drive-button"
                  >
                    <CloudOff className="w-4 h-4 mr-2" />
                    Desconectar
                  </Button>
                ) : (
                  <Button
                    onClick={handleConnectDrive}
                    className="bg-[#0F766E] hover:bg-[#0D6660]"
                    data-testid="connect-drive-button"
                  >
                    <Cloud className="w-4 h-4 mr-2" />
                    Conectar Google Drive
                  </Button>
                )}
              </div>
              
              {/* Backup Actions */}
              {driveConnected && (
                <div className="flex gap-3">
                  <Button
                    onClick={handleCreateBackup}
                    disabled={backupLoading}
                    variant="outline"
                    className="flex-1"
                    data-testid="create-backup-button"
                  >
                    {backupLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Criando backup...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Criar Backup Agora
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Backup History */}
              {backups.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#64748B] mb-2">Últimos Backups</h4>
                  <div className="space-y-2">
                    {backups.slice(0, 5).map((backup, idx) => (
                      <div key={backup.id || idx} className="flex items-center justify-between p-2 bg-[#F4F7F8] rounded">
                        <div>
                          <p className="text-sm font-medium">{backup.file_name}</p>
                          <p className="text-xs text-[#64748B]">
                            {new Date(backup.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        {backup.file_link && (
                          <a 
                            href={backup.file_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#0F766E] text-sm hover:underline"
                          >
                            Abrir
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0F766E] hover:bg-[#0D6660]"
              data-testid="save-config-button"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
